import React, { createContext, useContext, useEffect, useId, useMemo, useRef } from 'react';
import { useFormikContext } from 'formik';
import { cloneDeep, isEmpty, isEqual, omit } from 'lodash';
import { useHistory } from 'react-router-dom';

import { confirm } from '@dashboard/lib/confirm';

export const UNSAVED_CHANGES_MESSAGE = 'Discard unsaved changes?';

const UnsavedChangesContext = createContext(null);

/** For BrowserRouter's getUserConfirmation: the app's dialog instead of window.confirm. */
export function confirmNavigation(message, callback) {
  confirm(message, { confirm: 'Discard' }).then(callback);
}

// Forms inside report whether they hold unsaved edits; leaving for another screen
// while one does asks first. Moving within the same screen (a #section) never asks.
export function UnsavedChangesGuard({ children }) {
  const history = useHistory();
  const dirtyForms = useRef(new Set());

  const value = useMemo(
    () => ({
      report(formId, isDirty) {
        if (isDirty) dirtyForms.current.add(formId);
        else dirtyForms.current.delete(formId);
      },
    }),
    [],
  );

  useEffect(
    () =>
      history.block(next => {
        if (dirtyForms.current.size === 0) return undefined;
        if (next.pathname === history.location.pathname) return undefined;
        return UNSAVED_CHANGES_MESSAGE;
      }),
    [history],
  );

  return <UnsavedChangesContext.Provider value={value}>{children}</UnsavedChangesContext.Provider>;
}

export function useUnsavedChanges() {
  return useContext(UnsavedChangesContext);
}

// For forms on Formik directly (the Formik/Form wrapper reports by itself). A submit that
// ends without field errors counts as saved, so a form that keeps its values stops asking.
// `autosavedFields` save themselves outside Formik's submit, so they never count as unsaved.
export function UnsavedChangesReporter({ autosavedFields = [] }) {
  const unsavedChanges = useUnsavedChanges();
  const { values, initialValues, isSubmitting, submitCount, errors } = useFormikContext();
  const formId = useId();
  const savedValues = useRef(undefined);
  const settledSubmits = useRef(submitCount);

  const tracked = omit(values, autosavedFields);
  if (!isSubmitting && submitCount !== settledSubmits.current) {
    settledSubmits.current = submitCount;
    if (isEmpty(errors)) savedValues.current = cloneDeep(tracked);
  }

  const dirty = !isEqual(tracked, omit(initialValues, autosavedFields));
  unsavedChanges?.report(formId, dirty && !isEqual(tracked, savedValues.current));

  useEffect(() => () => unsavedChanges?.report(formId, false), [unsavedChanges, formId]);

  return null;
}
